import {
  Box,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { format } from 'date-fns-tz';
import { Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineQuestionMarkCircle } from 'react-icons/hi';

import Button from 'components/Button';
import { ControllableDrawer, DrawerFixedButton } from 'components/Drawer';
import { FormikDatepicker, FormikInput, FormikSwitch } from 'components/Formik';
import Tooltip from 'components/Tooltip';
import AssignEmployeesAndGroupsField from 'containers/JobsTasks/RightDrawer/AssignEmployeesAndGroupsField';
import { MONTH_DAY_YEAR_DASH_FORMAT } from 'global/appConstants';
import { useSaveHoliday } from 'hooks/timeOff/useSaveHoliday';
import { useCompanyInfo } from 'store/useUserStore';

import { initialHolidayData, holidayFormValidationSchema } from './schema';

interface IHoliday {
  onClose: () => void;
  formData?: typeof initialHolidayData;
}

const Holiday: React.FC<IHoliday> = ({ onClose, formData = initialHolidayData }) => {
  const { t } = useTranslation(['common', 'timeOff']);

  const companyInfo = useCompanyInfo();

  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const { status: saveHolidayStatus, mutateAsync: mutateAsyncSaveHoliday } = useSaveHoliday();

  const handleFormSubmit = async (values: typeof initialHolidayData) => {
    const postData = {
      id: values.id,
      uuid: values.uuid || undefined,
      name: values.name,
      start_date: values.start_date ? format(values.start_date, MONTH_DAY_YEAR_DASH_FORMAT) : '',
      end_date: values.end_date ? format(values.end_date, MONTH_DAY_YEAR_DASH_FORMAT) : '',
      active: values.active,
      on_calendar: values.on_calendar || false,
      user_ids: values.users ? values.users.map((user: any) => user.userId) : [],
      repeat_yearly: values.repeat_yearly,
    };

    mutateAsyncSaveHoliday(postData).then(() => {
      if (onClose) {
        onClose();
      }
    });
  };

  return (
    <Box overflowY="auto">
      <DrawerHeader>{formData?.id ? 'Update Holiday' : 'Create Holiday'}</DrawerHeader>
      <DrawerBody paddingBottom="150px">
        <Formik
          initialValues={formData}
          enableReinitialize
          validationSchema={holidayFormValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values }) => (
            <Form noValidate autoComplete="off">
              <FormikInput
                name="name"
                label="Holiday Name"
                formControlProps={{
                  isRequired: true,
                }}
              />

              <FormikDatepicker
                label={t('common:startDate')}
                name="start_date"
                datepickerProps={{
                  dateFormat: companyInfo?.companyDateFormat,
                  startDate: (values.start_date as Date) || null,
                  endDate: (values.end_date as Date) || null,
                  maxDate: (values.end_date as Date) || null,
                  placeholderText: 'Select Start Date',
                }}
                formControlProps={{
                  isRequired: true,
                }}
              />
              <FormikDatepicker
                label={t('common:endDate')}
                name="end_date"
                datepickerProps={{
                  dateFormat: companyInfo?.companyDateFormat,
                  startDate: (values.start_date as Date) || null,
                  endDate: (values.end_date as Date) || null,
                  minDate: (values.start_date as Date) || null,
                  placeholderText: 'Select End Date',
                }}
                formControlProps={{
                  isRequired: true,
                }}
              />

              <Box mb="6">
                <ControllableDrawer onClose={onDrawerClose} isOpen={isDrawerOpen}>
                  <DrawerContent>
                    <DrawerCloseButton />
                    <AssignEmployeesAndGroupsField />
                  </DrawerContent>
                </ControllableDrawer>
                <Text color="gray.500" fontSize="12px" fontWeight="600" mb="1" textTransform="uppercase">
                  Applicable For
                </Text>

                <InputGroup size="lg">
                  <Input
                    fontSize="sm"
                    variant="filled"
                    type="text"
                    border="1px"
                    borderColor="inherit"
                    onChange={() => null}
                    value={`${values.users.length || t('common:all')} ${
                      values.users.length === 1 ? t('common:user') : t('common:users')
                    }`}
                  />
                  <InputRightElement
                    width="auto"
                    mr="2"
                    children={
                      <Button size="sm" variant="link" onClick={onDrawerOpen}>
                        {values.users.length ? t('common:edit') : t('common:add')}
                      </Button>
                    }
                  />
                </InputGroup>
              </Box>

              <Box mb="4">
                <FormLabel
                  htmlFor="active"
                  color="gray.500"
                  fontSize="12px"
                  fontWeight="600"
                  mb="2"
                  textTransform="uppercase"
                >
                  Activate Holiday?
                </FormLabel>
                <FormikSwitch name="active" />
              </Box>
              <Box mb="4">
                <FormLabel
                  htmlFor="active"
                  color="gray.500"
                  fontSize="12px"
                  fontWeight="600"
                  mb="2"
                  textTransform="uppercase"
                  display="flex"
                  alignItems="center"
                >
                  Recurring yearly
                  <Tooltip
                    label={'Enable this option for holidays that repeat annually on the same calendar date.'}
                    placement="top"
                  >
                    <Box ml="1">
                      <Icon as={HiOutlineQuestionMarkCircle} height="18px" width="18px" color="gray.400" />
                    </Box>
                  </Tooltip>
                </FormLabel>
                <FormikSwitch name="repeat_yearly" />
              </Box>

              <DrawerFixedButton>
                <Button
                  colorScheme="secondary"
                  type="submit"
                  width="100%"
                  size="lg"
                  isLoading={saveHolidayStatus === 'loading'}
                >
                  {formData?.id ? 'Update Holiday' : 'Add Holiday'}
                </Button>
              </DrawerFixedButton>
            </Form>
          )}
        </Formik>
      </DrawerBody>
    </Box>
  );
};
export default Holiday;
